// ============================================================
// 🆕 INICIO: Auth Interceptor - Auditoría Seguridad Fase 5 - 2026-03-15
// ============================================================
// Intercepta respuestas 401/403 del backend para:
// - 401 (Token inválido/expirado): Logout automático + redirect a login
// - 403 (Sin permisos): Notificación al usuario sin logout
// Usa Injector lazy para evitar dependencia circular con HttpClient.
// Autor: Claude Code
// ============================================================

import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/services/shared.service';
import { MensajesService } from 'src/app/services/mensajes.service';

// ============================================================
// 🆕 INICIO: Codigos de seguridad Fase 0 usuarios - 2026-09-01 (MDE-489)
// ============================================================
// El backend devuelve el mensaje de error con el formato "CODIGO: texto".
// Extrae solo el codigo (antes de los dos puntos).
function extractBackendCode(error: HttpErrorResponse): string {
  const backendMessage: string = (error?.error?.error?.message as string) || '';
  return backendMessage.split(':')[0]?.trim() || '';
}
// ============================================================
// 🆕 FIN: Codigos de seguridad Fase 0 usuarios
// ============================================================

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private _injector: Injector,
    private _router: Router,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // ============================================================
          // 🆕 INICIO: Excepcion cambio de clave - 2026-09-01 (MDE-489)
          // ============================================================
          // Un 401 en auth/change-password es "clave actual incorrecta"
          // (INVALID_CREDENTIALS), no un token invalido: NO cerrar sesion,
          // lo procesa el propio formulario.
          if (req.url.includes('auth/change-password')) {
            return throwError(() => error);
          }
          // 🆕 2026-09-04 (MDE-513): un 401 en auth/mfa/* es "codigo no valido"
          // (MFA_CODE_INVALID) o "token de reto vencido", no una sesion caida:
          // lo procesan la pagina del reto y la tarjeta del perfil. Sin esta
          // excepcion, teclear mal el codigo expulsaba al login (medido en QA).
          if (req.url.includes('auth/mfa/')) {
            return throwError(() => error);
          }
          // 🆕 2026-09-24: las rutas PUBLICAS de invitacion (validar/aceptar) no
          // exigen token; un 401 aqui es de la peticion, no de una sesion caida.
          // El componente lo maneja mostrando "invitacion invalida".
          if (req.url.includes('auth/invitation')) {
            return throwError(() => error);
          }
          // 🆕 2026-10-08 (MDE-605): las rutas PUBLICAS del registro de personal por
          // enlace (/public/registro/<codigo>/...) tienen su propia sesion
          // (X-Registro-Sesion). Un 401 ahi es «la sesion del enlace vencio», no una
          // sesion de VIVO caida: el formulario lo maneja con «vuelve a empezar». Sin
          // esta excepcion, un tecnico con la sesion de VIVO abierta en el celular
          // seria expulsado al login cuando vence la del enlace.
          if (req.url.includes('public/registro/')) {
            return throwError(() => error);
          }
          // ============================================================
          // 🆕 FIN: Excepcion cambio de clave
          // ============================================================

          // 🆕 2026-09-24 (bug invitacion): solo expulsar al login si REALMENTE
          // habia una sesion (token guardado). Sin token, el usuario esta en una
          // ruta publica legitima (aceptar-invitacion, landing, etc.) y una
          // llamada de arranque que reciba 401 no debe forzar logout+redirect:
          // ese salto era lo que mandaba al login al abrir el enlace del correo.
          const sharedService = this._injector.get(SharedService);
          const hadSession = !!sharedService.getToken();
          if (!hadSession) {
            return throwError(() => error);
          }
          // Token inválido o expirado → logout automático
          sharedService.logOutUser();
          this._router.navigate(['/public/login']);
        } else if (error.status === 403) {
          const code = extractBackendCode(error);
          const mensajes = this._injector.get(MensajesService);

          // ============================================================
          // 🆕 INICIO: Codigos de seguridad Fase 0 usuarios - 2026-09-01 (MDE-489)
          // ============================================================
          if (code === 'PASSWORD_CHANGE_REQUIRED') {
            // El backend exige cambiar la clave antes de continuar.
            this._router.navigate(['/public/cambiar-clave']);
          } else if (code === 'NO_ACTIVE_ROLE') {
            // Sin rol activo asignado: mensaje claro y cierre de sesion.
            const translate = this._injector.get(TranslateService);
            const sharedService = this._injector.get(SharedService);
            mensajes.mensajes_warning(translate.instant('AUTH.SESSION.noActiveRole'));
            sharedService.logOutUser();
            this._router.navigate(['/public/login']);
          } else {
            // 🆕 2026-09-25 (F2-408): permiso denegado sobre un RECURSO. Solo se
            // avisa al usuario si fue una ACCIÓN explícita suya (escritura:
            // POST/PUT/PATCH/DELETE). Para cargas de fondo (GET) el 403 se degrada
            // EN SILENCIO —el componente ya maneja el recurso faltante mostrando la
            // sección vacía— y NO se dispara el modal "No tiene permisos", que era
            // el mensaje molesto al abrir pantallas como Talleres (también F2-423).
            const isWriteAction = req.method !== 'GET';
            if (isWriteAction) {
              const translate = this._injector.get(TranslateService);
              if (
                code === 'MODULE_PERMISSION_DENIED' ||
                code === 'ROLE_NOT_IN_COMMAND' ||
                code === 'GLOBAL_ROLE_REQUIRES_SUPER' ||
                // 🆕 2026-09-02 (MDE-496): rol bloqueado por plantilla (roles-admin/roles).
                code === 'ROLE_TEMPLATE_LOCKED' ||
                // 🆕 2026-10-23 (S.7, MDE-637): fila de plataforma desde una organizacion.
                code === 'PLATFORM_ROW_REQUIRES_SUPER'
              ) {
                const key = code === 'MODULE_PERMISSION_DENIED'
                  ? 'AUTH.SESSION.modulePermissionDenied'
                  : code === 'ROLE_NOT_IN_COMMAND'
                    ? 'AUTH.SESSION.roleNotInCommand'
                    : code === 'GLOBAL_ROLE_REQUIRES_SUPER'
                      ? 'AUTH.SESSION.globalRoleRequiresSuper'
                      : code === 'PLATFORM_ROW_REQUIRES_SUPER'
                        ? 'AUTH.SESSION.platformRowRequiresSuper'
                        : 'AUTH.SESSION.roleTemplateLocked';
                mensajes.mensajes_info(translate.instant(key));
              } else {
                mensajes.mensajes_info('No tiene permisos para realizar esta acción.');
              }
            }
          }
          // ============================================================
          // 🆕 FIN: Codigos de seguridad Fase 0 usuarios
          // ============================================================
        }
        return throwError(() => error);
      }),
    );
  }
}

// ============================================================
// 🆕 FIN: Auth Interceptor
// ============================================================
